import { storeToRefs } from 'pinia'
import { useAppStore } from '../stores/appStore'
import { useChatStore } from '../stores/chatStore'
import type { Message } from '../types/chat/Message'
import { MessageType } from '../types/chat/MessageType'

const blockTags = new Set([
  'P',
  'DIV',
  'LI',
  'UL',
  'OL',
  'PRE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TABLE',
  'TR',
  'TD',
  'TH',
])

function getAnchorExportText(anchor: HTMLAnchorElement): string {
  const label = anchor.textContent?.trim() || ''
  const href = anchor.getAttribute('href')?.trim() || ''

  if (!href) return label
  if (!label) return href
  if (label === href) return href

  return `${label} (${href})`
}

function htmlToPlainText(html: string): string {
  if (!html) return ''

  const root = document.createElement('div')
  root.innerHTML = html

  root.querySelectorAll('a').forEach(anchor => {
    anchor.replaceWith(document.createTextNode(getAnchorExportText(anchor)))
  })

  root.querySelectorAll('br').forEach(br => {
    br.replaceWith(document.createTextNode('\n'))
  })

  let output = ''
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
  let node: Node | null

  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      output += (node as Text).data
      continue
    }

    if (node.nodeType === Node.ELEMENT_NODE && blockTags.has((node as Element).tagName)) {
      if (!output.endsWith('\n')) output += '\n'
    }
  }

  return output
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function exportChatText() {
  const chatStore = useChatStore()
  const appStore = useAppStore()
  const { messages } = storeToRefs(chatStore)

  const transcriptMessagePairs = appStore.configuration?.components.supportRequest.messagePairs || 3

  function buildAlternatingMessages() {
    let output = ''

    const cleanedMessages: Message[] = messages.value
      .filter(m => !m.isLoading && !!m.text)
      .map(m => ({
        ...m,
        text: htmlToPlainText(m.text).trim(),
      }))
      .filter(m => m.text.length > 0)

    if (!cleanedMessages.length) {
      console.warn('No messages to export')
      return ''
    }

    const totalPairs = Math.floor(cleanedMessages.length / 2)
    const pairsToUse = Math.min(transcriptMessagePairs, totalPairs)

    const sliceSize = pairsToUse * 2
    const sourceMessages = cleanedMessages.slice(-sliceSize)

    sourceMessages.forEach(msg => {
      const raw = msg.text
      const isUser = msg.type === MessageType.User
      const caption = isUser ? 'User prompt' : 'Agent response'

      output += `• ${caption}:\n${raw}\n\n`
    })

    return output.trimEnd()
  }

  return { buildAlternatingMessages }
}
